import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Shield,
  Phone,
  MessageCircle,
  Bell,
  CreditCard,
  Car,
  Bike,
  Check,
  ArrowRight,
  ShoppingCart,
  Link2,
  ScanLine,
  AlertTriangle,
  Lock,
  QrCode
} from 'lucide-react';
import { API } from '../App';
import BrandLogo from '../components/BrandLogo';

const LandingPage = () => {
  const navigate = useNavigate();

  const [pricing, setPricing] = useState({
    basePricePaise: 34900,
    offerPricePaise: null,
    durationDays: 365,
    offerEnabled: false,
    offerScope: 'global'
  });

  useEffect(() => {
    const loadPricing = async () => {
      try {
        const response = await axios.get(`${API}/settings/public`);
        setPricing({
          basePricePaise: response.data.subscription_price_paise,
          offerPricePaise: response.data.offer_enabled ? response.data.offer_price_paise : null,
          durationDays: response.data.subscription_duration_days,
          offerEnabled: response.data.offer_enabled,
          offerScope: response.data.offer_scope
        });
      } catch (error) {
        // Keep defaults
      }
    };

    loadPricing();
  }, []);

  const formatPrice = (paise) => Math.round(paise / 100);
  const hasOffer = pricing.offerEnabled && pricing.offerPricePaise;
  const displayPrice = hasOffer ? pricing.offerPricePaise : pricing.basePricePaise;
  const offerLabel = pricing.offerScope === 'new_users' ? 'New User Offer' : 'Limited Offer';

  const heroFeatures = [
    {
      icon: <Lock className="w-5 h-5" />,
      title: 'Your number stays private'
    },
    {
      icon: <Phone className="w-5 h-5" />,
      title: 'Call or message instantly'
    },
    {
      icon: <Bell className="w-5 h-5" />,
      title: 'Get alerts when scanned'
    },
    {
      icon: <CreditCard className="w-5 h-5" />,
      title: 'No monthly fees. Simple yearly plan'
    }
  ];

  const howItWorks = [
    {
      icon: <ShoppingCart className="w-6 h-6" />,
      title: 'Buy your QR sticker',
      description: 'Choose your SIDEO QR sticker and place your order quickly.'
    },
    {
      icon: <Link2 className="w-6 h-6" />,
      title: 'Link your phone number',
      description: 'Securely link your number to the QR profile.'
    },
    {
      icon: <Car className="w-6 h-6" />,
      title: 'Stick it on your vehicle',
      description: 'Apply it to your car, bike, or scooter in seconds.'
    },
    {
      icon: <ScanLine className="w-6 h-6" />,
      title: 'Get contacted via scan',
      description: 'People can call or message you instantly after scanning.'
    }
  ];

  const problemScenarios = [
    {
      icon: <Car className="w-6 h-6" />,
      text: 'A parked vehicle blocks someone and they cannot find the owner.'
    },
    {
      icon: <AlertTriangle className="w-6 h-6" />,
      text: 'Someone needs to urgently reach the owner in an emergency.'
    },
    {
      icon: <Shield className="w-6 h-6" />,
      text: 'Phone numbers written on dashboards expose personal privacy.'
    }
  ];

  const detailedFeatures = [
    {
      icon: <Lock className="w-6 h-6" />,
      title: 'Privacy Protection',
      description: 'Your phone number is never shown to scanners.'
    },
    {
      icon: <Phone className="w-6 h-6" />,
      title: 'Flexible Contact',
      description: 'Call and message options from one scan.'
    },
    {
      icon: <Bell className="w-6 h-6" />,
      title: 'Instant Alerts',
      description: 'Get immediate scan alerts when someone reaches out.'
    },
    {
      icon: <Bike className="w-6 h-6" />,
      title: 'Works Everywhere',
      description: 'Built for cars, bikes, and scooters.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 w-full border-b border-slate-200/80 bg-white/95 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <BrandLogo className="h-12 w-auto" />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/activate-qr')}
                className="btn-secondary h-10 px-4 text-sm"
                data-testid="header-activate-btn"
              >
                Activate QR
              </button>
              <button
                onClick={() => navigate('/login')}
                className="btn-primary h-10 px-5 text-sm"
                data-testid="header-login-btn"
              >
                Login
              </button>
            </div>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 via-white to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="animate-fade-in">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight mb-5" style={{ fontFamily: 'Barlow' }}>
                Stay reachable without sharing your number
              </h1>
              <p className="text-lg md:text-xl leading-relaxed text-slate-600 mb-8 max-w-xl">
                Blocked parking? Urgent reach-out needed? Let people call or message you instantly while your personal number stays private.
              </p>
              <div className="flex flex-col items-start gap-3">
                <button
                  onClick={() => navigate('/buy-qr')}
                  className="btn-primary text-lg px-8 h-14 flex items-center justify-center"
                  data-testid="hero-get-started-btn"
                >
                  Buy Your QR Sticker
                  <ArrowRight className="w-5 h-5 ml-2" />
                </button>
                <button
                  onClick={() => navigate('/activate-qr')}
                  className="text-sm font-medium text-slate-600 hover:text-slate-900 underline underline-offset-4 transition-colors"
                  data-testid="hero-activate-link"
                >
                  Already have a QR? Activate here
                </button>
              </div>
            </div>

            <div className="relative">
              <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-100 to-white p-4 sm:p-6 shadow-xl">
                <div className="rounded-2xl bg-white p-4 sm:p-5 shadow-sm border border-slate-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-slate-800 font-semibold">
                      <Car className="w-5 h-5" />
                      Parked Vehicle
                    </div>
                    <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800">SIDEO QR</span>
                  </div>
                  <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-slate-900">QR Sticker</p>
                        <p className="text-xs text-slate-600">Windshield / Bike body</p>
                      </div>
                      <div className="relative">
                        <div className="absolute -inset-2 rounded-xl bg-blue-400/20 blur-md"></div>
                        <div className="relative w-14 h-14 rounded-lg bg-white border border-slate-300 flex items-center justify-center">
                          <QrCode className="w-8 h-8 text-slate-800" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <p className="text-sm font-medium text-slate-700 mb-3">Someone scanned your vehicle QR</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" className="h-10 rounded-xl bg-[#0DB504] text-white text-sm font-semibold flex items-center justify-center gap-2">
                      <Phone className="w-4 h-4" />
                      Call now
                    </button>
                    <button type="button" className="h-10 rounded-xl border border-slate-300 text-slate-800 text-sm font-semibold flex items-center justify-center gap-2">
                      <MessageCircle className="w-4 h-4" />
                      Send message
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-14 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {heroFeatures.map((item, index) => (
              <div
                key={item.title}
                className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex items-center gap-3"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-9 h-9 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center">
                  {item.icon}
                </div>
                <p className="text-sm font-medium text-slate-700 leading-snug">{item.title}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500 mt-3">*Annual renewal applies</p>
        </div>
      </section>

      <section id="pricing" className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div className="space-y-4">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Barlow' }}>
                SIDEO QR Sticker
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed">
                A physical QR sticker that keeps you reachable without revealing your number.
              </p>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-slate-900">Car Windshield</p>
                      <p className="text-xs text-slate-500">High-visibility placement</p>
                    </div>
                    <Car className="w-6 h-6 text-slate-700" />
                  </div>
                  <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-3 flex items-center justify-center">
                    <QrCode className="w-10 h-10 text-slate-800" />
                  </div>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-slate-900">Bike Body</p>
                      <p className="text-xs text-slate-500">Compact, durable placement</p>
                    </div>
                    <Bike className="w-6 h-6 text-slate-700" />
                  </div>
                  <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-3 flex items-center justify-center">
                    <QrCode className="w-10 h-10 text-slate-800" />
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="text-sm font-medium text-slate-700 mb-3">Scan Result Preview</p>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" className="h-10 rounded-xl bg-[#0DB504] text-white text-sm font-semibold flex items-center justify-center gap-2">
                    <Phone className="w-4 h-4" />
                    Call now
                  </button>
                  <button type="button" className="h-10 rounded-xl border border-slate-300 text-slate-800 text-sm font-semibold flex items-center justify-center gap-2">
                    <MessageCircle className="w-4 h-4" />
                    Send message
                  </button>
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-blue-200 bg-white p-6 sm:p-8 shadow-lg">
              {hasOffer && (
                <div className="mb-4">
                  <span className="bg-emerald-100 text-emerald-800 text-xs font-bold px-3 py-1 rounded-full">
                    {offerLabel}
                  </span>
                </div>
              )}

              <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow' }}>
                SIDEO QR Sticker
              </h3>
              <p className="text-slate-600 mb-4">A clean, privacy-first way to stay reachable on the road.</p>

              <div className="mb-2">
                {hasOffer && (
                  <span className="text-slate-400 line-through mr-2">₹{formatPrice(pricing.basePricePaise)}</span>
                )}
                <span className="text-4xl font-bold text-slate-900">₹{formatPrice(displayPrice)}</span>
                <span className="text-slate-500 ml-2">per sticker / year</span>
              </div>

              <p className="text-sm text-slate-500 mb-5">
                Includes {Math.round(pricing.durationDays / 365)} year access · Renews yearly at a minimal cost
              </p>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start space-x-3">
                  <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-emerald-600" />
                  </div>
                  <span className="text-slate-700">Physical sticker included</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-emerald-600" />
                  </div>
                  <span className="text-slate-700">Privacy-protected contact</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-emerald-600" />
                  </div>
                  <span className="text-slate-700">Call & message support</span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-emerald-600" />
                  </div>
                  <span className="text-slate-700">Easy setup</span>
                </li>
              </ul>

              <button
                onClick={() => navigate('/buy-qr')}
                className="w-full h-12 rounded-xl bg-[#0DB504] text-white font-semibold hover:bg-[#0a9e03] transition-colors flex items-center justify-center gap-2"
                data-testid="buy-qr-btn"
              >
                Buy Your QR Sticker
                <ArrowRight className="w-4 h-4" />
              </button>

              <div className="mt-4 text-center">
                <p className="text-sm text-slate-600 mb-2">Already purchased your QR sticker?</p>
                <button
                  onClick={() => navigate('/activate-qr')}
                  className="w-full h-11 rounded-xl border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 transition-colors"
                  data-testid="pricing-activate-btn"
                >
                  Activate QR Code
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="how-it-works" className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Barlow' }}>
              How It Works
            </h2>
            <p className="text-lg text-slate-600">4 simple steps to make your vehicle reachable</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {howItWorks.map((item, index) => (
              <div
                key={item.title}
                className="p-5 rounded-2xl border border-slate-200 bg-slate-50 shadow-sm"
                data-testid={`how-it-works-step-${index}`}
              >
                <div className="w-11 h-11 rounded-xl bg-blue-100 text-blue-800 flex items-center justify-center mb-4">
                  {item.icon}
                </div>
                <div className="text-sm font-medium text-blue-700 mb-2">Step {index + 1}</div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Barlow' }}>
                  {item.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Barlow' }}>
              Common Problems SIDEO QR Solves
            </h2>
            <p className="text-lg text-slate-600">Built for everyday parking and urgent contact scenarios</p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {problemScenarios.map((scenario) => (
              <div key={scenario.text} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center mb-4">
                  {scenario.icon}
                </div>
                <p className="text-slate-700 leading-relaxed">{scenario.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Barlow' }}>
              Features Built for Everyday Vehicle Owners
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {detailedFeatures.map((feature) => (
              <div key={feature.title} className="rounded-2xl border border-slate-200 bg-slate-50 p-5 shadow-sm">
                <div className="w-10 h-10 rounded-xl bg-blue-100 text-blue-800 flex items-center justify-center mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Barlow' }}>
                  {feature.title}
                </h3>
                <p className="text-sm text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Barlow' }}>
              Trusted by Vehicle Owners
            </h2>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <div className="rounded-2xl bg-white border border-slate-200 p-6 shadow-sm text-center">
              <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Barlow' }}>1000+</p>
              <p className="text-sm text-slate-600 mt-1">Vehicles using SIDEO</p>
            </div>
            <div className="rounded-2xl bg-white border border-slate-200 p-6 shadow-sm text-center">
              <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Barlow' }}>Private</p>
              <p className="text-sm text-slate-600 mt-1">Owner number is never shown</p>
            </div>
            <div className="rounded-2xl bg-white border border-slate-200 p-6 shadow-sm text-center">
              <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Barlow' }}>Yearly</p>
              <p className="text-sm text-slate-600 mt-1">No monthly subscription fees</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Barlow' }}>
            Make your vehicle reachable today
          </h2>
          <p className="text-lg text-slate-300 leading-relaxed mb-8">
            Make your vehicle easy to reach for parking and emergencies while keeping your personal number protected.
          </p>

          <div className="flex flex-col sm:flex-row justify-center items-center gap-3">
            <button
              onClick={() => navigate('/buy-qr')}
              className="h-12 px-8 rounded-xl bg-white text-slate-900 font-semibold hover:bg-slate-100 transition-colors flex items-center justify-center gap-2"
              data-testid="trust-buy-cta-btn"
            >
              Order Your QR Sticker
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate('/activate-qr')}
              className="h-12 px-8 rounded-xl border border-slate-300 text-slate-200 font-medium hover:bg-slate-700/40 transition-colors"
              data-testid="trust-activate-cta-btn"
            >
              Activate Your QR
            </button>
          </div>
        </div>
      </section>

      <section id="support" className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Barlow' }}>
              Need Help?
            </h2>
            <p className="text-lg text-slate-600">
              Have questions about your QR sticker or need help with activation? We're here to help.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="w-11 h-11 rounded-xl bg-blue-100 text-blue-800 flex items-center justify-center mb-4">
                <span className="text-lg" aria-hidden="true">📧</span>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Barlow' }}>
                Email Us
              </h3>
              <p className="text-sm text-slate-600 mb-4">For order, activation, or support queries.</p>
              <a
                href="mailto:support@sideo.in"
                className="inline-flex items-center text-black font-medium hover:text-gray-800 hover:underline underline-offset-4"
              >
                support@sideo.in
              </a>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="w-11 h-11 rounded-xl bg-blue-100 text-blue-800 flex items-center justify-center mb-4">
                <span className="text-lg" aria-hidden="true">💬</span>
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Barlow' }}>
                Activation Help
              </h3>
              <p className="text-sm text-slate-600 mb-4">Already purchased your sticker? Activate and manage your QR from your account.</p>
              <button
                onClick={() => navigate('/activate-qr')}
                className="h-10 px-4 rounded-xl border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 transition-colors"
                data-testid="support-activate-btn"
              >
                Activate QR Code
              </button>
            </div>
          </div>

          <div className="text-center mt-6 space-y-1">
            <p className="text-sm text-slate-600">We typically respond within 24 hours.</p>
            <p className="text-sm text-slate-500">We’re here to help you get set up quickly and resolve any issues.</p>
          </div>
        </div>
      </section>

      <footer className="bg-white border-t border-slate-200 py-8 px-4">
        <div className="max-w-7xl mx-auto text-center text-slate-600">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <BrandLogo className="h-6 w-auto" />
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mb-4 text-sm">
            <Link to="/terms-and-conditions" className="text-slate-600 hover:text-slate-900 hover:underline underline-offset-4">
              Terms and Conditions
            </Link>
            <Link to="/privacy-policy" className="text-slate-600 hover:text-slate-900 hover:underline underline-offset-4">
              Privacy Policy
            </Link>
            <Link to="/refund-replacement-policy" className="text-slate-600 hover:text-slate-900 hover:underline underline-offset-4">
              Refund and Replacement Policy
            </Link>
            <a href="#support" className="text-slate-600 hover:text-slate-900 hover:underline underline-offset-4">
              Contact / Support
            </a>
          </div>
          <p className="text-sm">© 2026 sideo. Reachable without revealing your phone number.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
